import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Tabs, Tab, Alert, Button, Chip, Stack,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import Layout from '../../components/layout/Layout';
import { features } from '../../config/features';
import { SERVICE_CATEGORIES, CATEGORY_ORDER } from '../../constants/serviceRequestTaxonomy';
import PriceRequestForm from '../storeportal/PriceRequestForm';
import { getStores } from '../../Redux/Actions/storeAction';
import { getChangeRequests } from '../../Redux/Actions/changeRequestAction';

const PLACEHOLDER = {
  menu_catalog: '메뉴 마스터에서 다중 선택 · 추가/삭제 요청 (Phase 1)',
  seasonal: '시즈널 캠페인 선택 → 개별 SKU 참여/보류/가격 (Phase 2)',
  media: '스크린·슬롯 선택 + 사진/메모 업로드 (Phase 2)',
};

function storePortalUserId(u) {
  if (!u) return null;
  if (u.role === 'admin') return u._id;
  return u.user?._id || u._id;
}

function storePortalOpts(u) {
  if (!u) return {};
  if (u.role === 'admin') return {};
  return { role: u.role, subuserId: u._id, tljRegion: u.tljRegion };
}

function ServiceRequestHub({ user }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { stores } = useSelector((s) => s.Store);
  const [tab, setTab] = useState('menu_price');
  const [storeId, setStoreId] = useState('');

  const userId = storePortalUserId(user);
  const opts = storePortalOpts(user);

  useEffect(() => {
    if (userId) dispatch(getStores(userId, opts));
  }, [dispatch, userId, opts.role, opts.subuserId, opts.tljRegion]);

  const myStores = useMemo(() => (stores || []).filter(Boolean), [stores]);
  const selectedStore = myStores.find((s) => s._id === storeId) || myStores[0];

  useEffect(() => {
    if (myStores.length && !storeId) setStoreId(myStores[0]._id);
  }, [myStores, storeId]);

  if (!features.serviceRequestV2) {
    return <Navigate to="/requests" replace />;
  }

  return (
    <Layout user={user}>
      <Box sx={{ maxWidth: 960, mx: 'auto', py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={700}>서비스 요청</Typography>
          <Chip size="small" label="v2 preview" color="primary" variant="outlined" />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          가격 · 메뉴 · 시즈널 · 미디어를 한 곳에서 분류해 접수합니다. 운영 서버 배포 전 feature 브랜치 전용.
        </Typography>

        <Paper sx={{ mb: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {CATEGORY_ORDER.map((id) => (
              <Tab key={id} value={id} label={SERVICE_CATEGORIES[id].label} />
            ))}
          </Tabs>
        </Paper>

        {myStores.length > 1 && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>매장</InputLabel>
            <Select
              label="매장"
              value={selectedStore?._id || ''}
              onChange={(e) => setStoreId(e.target.value)}
            >
              {myStores.map((s) => (
                <MenuItem key={s._id} value={s._id}>{s.name} ({s.tljNo})</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {tab === 'menu_price' && selectedStore && (
          <Paper sx={{ p: 2 }}>
            <PriceRequestForm
              store={selectedStore}
              userId={userId}
              user={user}
              subuserId={opts.subuserId}
              onSubmitted={() => dispatch(getChangeRequests(userId, { subuserId: opts.subuserId }))}
            />
          </Paper>
        )}
        {tab === 'menu_price' && !selectedStore && (
          <Alert severity="warning">선택 가능한 매장이 없습니다.</Alert>
        )}

        {tab !== 'menu_price' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {PLACEHOLDER[tab]}
          </Alert>
        )}

        <Button variant="text" onClick={() => navigate('/requests')} sx={{ mt: 2 }}>
          기존 Requests 보드로 이동
        </Button>
      </Box>
    </Layout>
  );
}

export default ServiceRequestHub;
